import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiArrowUpRight, FiUser } from 'react-icons/fi';
import { usePropsStore } from '../stores/propsStore';
import { Sport } from '../domain/types';
import { PlayerPhoto } from '../components/PlayerPhoto';
import { PropDetail } from './SportScreen';
import { useAuthStore } from '../stores/authStore';
import { hapticTap } from '../utils/haptics';
import { FaFire } from 'react-icons/fa';
import { Side } from '../domain/types';

type News = { id: string; headline: string; description?: string; link: string; image?: string; sport: Sport };

const ESPN = 'https://site.api.espn.com/apis/site/v2/sports';
const MIN_HIT_SAMPLE = 10;

export function HomeScreen() {
  const navigate = useNavigate();
  const { allProps, isLoading, lastFetched } = usePropsStore();
  const { token, profile, profilePhotoUrl } = useAuthStore();
  const [avatarUrl, setAvatarUrl] = useState('');
  const shortUserName = profile?.firstName?.trim() || profile?.username?.split(/[._\s]/)[0] || 'VISITANTE';
  const [news, setNews] = useState<News[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [schedule, setSchedule] = useState<Record<Sport, number>>({ NBA: 0, NHL: 0 });
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | Sport>('all');
  const [activePick, setActivePick] = useState<(typeof allProps)[number] | null>(null);

  useEffect(() => {
    if (!token || !profile?.hasProfilePhoto) { setAvatarUrl(''); return; }
    let objectUrl = '';
    let cancelled = false;
    fetch(`${profilePhotoUrl()}?v=${profile.photoUpdatedAt ?? ''}`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' })
      .then(response => response.ok ? response.blob() : Promise.reject())
      .then(blob => { if (!cancelled) { objectUrl = URL.createObjectURL(blob); setAvatarUrl(objectUrl); } })
      .catch(() => setAvatarUrl(''));
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [token, profile?.hasProfilePhoto, profile?.photoUpdatedAt, profilePhotoUrl]);

  const loadNews = async () => {
    setNewsLoading(true);
    const feeds = await Promise.all([Sport.NBA, Sport.NHL].map(async sport => {
      const path = sport === Sport.NBA ? 'basketball/nba' : 'hockey/nhl';
      try {
        const response = await fetch(`${ESPN}/${path}/news?limit=12&region=br&lang=pt`);
        if (!response.ok) return [];
        const body = await response.json();
        return (body.articles ?? []).flatMap((article: any) => {
          const link = article.links?.web?.href;
          if (!article.headline || !link) return [];
          return [{ id: `${sport}-${article.id ?? link}`, headline: article.headline, description: article.description, link, image: article.images?.[0]?.url, sport }];
        });
      } catch { return []; }
    }));
    setNews(feeds.flat().slice(0, 12));
    setNewsLoading(false);
  };

  useEffect(() => {
    void loadNews();
    const date = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()).replace(/-/g, '');
    Promise.all([Sport.NBA, Sport.NHL].map(async sport => {
      const path = sport === Sport.NBA ? 'basketball/nba' : 'hockey/nhl';
      try {
        const response = await fetch(`${ESPN}/${path}/scoreboard?limit=100&dates=${date}`);
        const body = response.ok ? await response.json() : {};
        const games = (body.events ?? []).filter((event: any) => {
          const competitors = event.competitions?.[0]?.competitors ?? [];
          const validTeam = (side: string) => competitors.some((item: any) =>
            item.homeAway === side && Boolean(item.team?.displayName || item.team?.name),
          );
          return validTeam('away') && validTeam('home');
        });
        return [sport, games.length] as const;
      } catch { return [sport, 0] as const; }
    })).then(entries => setSchedule(Object.fromEntries(entries) as Record<Sport, number>)).finally(() => setScheduleLoading(false));
  }, []);

  const topProps = useMemo(() => allProps
    .filter(p => filter === 'all' || p.sport === filter)
    .filter(p => p.odds >= 1.4 && p.odds <= 2.2 && (p.side === Side.OVER ? p.overSample ?? 0 : p.underSample ?? 0) >= MIN_HIT_SAMPLE && (p.hitRatePct ?? 0) >= 60)
    .sort((a, b) => (b.hitRatePct ?? 0) - (a.hitRatePct ?? 0) || Math.max(b.overSample ?? 0,b.underSample ?? 0) - Math.max(a.overSample ?? 0,a.underSample ?? 0))
    .slice(0, 6), [allProps, filter]);
  return (
    <main className="screen home-screen">
      <div className="android-app-header"><img src="/rushprops-mark-white.png" alt="Rush Props" /><div className="home-profile-chip"><button className="home-profile-avatar" aria-label="Abrir perfil" onClick={() => navigate('/profile')}>{avatarUrl ? <img src={avatarUrl} alt=""/> : <FiUser />}</button><span>{shortUserName}</span></div></div>
      <header className="home-welcome">
        <h1>BEM-VINDO(A)</h1>
        <p className="home-date">{new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' }).format(new Date()).toUpperCase()}</p>
        {lastFetched && <span className="home-updated">Atualizado às {new Date(lastFetched).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>}
      </header>

      <section className="schedule-pair" aria-label="Jogos de hoje">
        {([Sport.NBA, Sport.NHL] as Sport[]).map(sport => <button key={sport} className={`schedule-tile ${sport.toLowerCase()}`} onClick={() => navigate(`/schedule/${sport.toLowerCase()}`)}>
          <span>{sport}</span><small>{scheduleLoading ? 'Buscando partidas…' : `${schedule[sport]} partidas hoje`}</small><FiArrowUpRight className="tile-arrow" />
        </button>)}
      </section>

      <section className="home-section">
        <div className="section-heading"><h2>NOTÍCIAS</h2><button className="text-action" onClick={() => void loadNews()} disabled={newsLoading}><FiRefreshCw className={newsLoading ? 'turning' : ''} /> ESPN · ATUALIZAR</button></div>
        {news.length ? <div className="news-rail">{news.map(item => <a className="news-card" key={item.id} href={item.link} target="_blank" rel="noreferrer">
          {item.image ? <img src={item.image} alt="" loading="lazy" /> : <div className="news-placeholder">{item.sport === Sport.NBA ? 'NBA' : 'NHL'}</div>}
          <span className={`sport-kicker ${item.sport.toLowerCase()}`}>{item.sport}</span><strong>{item.headline}</strong>
        </a>)}</div> : <div className="news-empty">{newsLoading ? 'Carregando notícias da NBA e NHL…' : 'Notícias indisponíveis no momento.'}</div>}
      </section>

      <section className="sport-filters" aria-label="Filtrar por liga">
        {([{ id: 'all', label: 'TODOS' }, { id: Sport.NBA, label: 'NBA' }, { id: Sport.NHL, label: 'NHL' }] as const).map(item => <button key={item.id} className={filter === item.id ? 'selected' : ''} onClick={() => { hapticTap(); setFilter(item.id); }}>{item.label}</button>)}
      </section>

      {topProps[0] && <section className="featured-pick">
        <span className="eyebrow">ESCOLHA EM DESTAQUE · {topProps[0].sport}</span>
        <div className="featured-main"><div><h2>{topProps[0].player.toUpperCase()}</h2><p>{topProps[0].market} · {topProps[0].game}</p></div><strong>{topProps[0].hitRatePct}%<small>HIT RATE</small></strong></div>
        <div className="featured-line"><b>{topProps[0].side.toUpperCase()} {topProps[0].line}</b><span>ODD {topProps[0].odds.toFixed(2)}</span></div>
        <button onClick={() => setActivePick(topProps[0])}>VER ANÁLISE <FiArrowUpRight /></button>
      </section>}

      <section className="home-section top-props-section"><div className="section-heading"><h2>TOP PROPS</h2><span>POR HIT RATE</span></div>
        <div className="top-props-list">{topProps.map((pick, index) => {
          return <button className="top-prop-row" key={pick.id} onClick={() => setActivePick(pick)}>
            <span className="top-prop-rank">{String(index + 1).padStart(2, '0')}</span><PlayerPhoto pick={pick} className="home-player-photo" />
            <span className="top-prop-info"><b>{pick.player.toUpperCase()}</b><small>{pick.market} · {pick.game}</small><small>{pick.side.toUpperCase()} {pick.line} · @{pick.odds.toFixed(2)} · {pick.side === Side.OVER ? pick.overHits ?? 0 : pick.underHits ?? 0}/{pick.side === Side.OVER ? pick.overSample ?? 0 : pick.underSample ?? 0} jogos</small></span>
            {getHitStreak(pick.historyValues, pick.line, pick.side) >= 3 && <span className="prop-streak" title={`${getHitStreak(pick.historyValues, pick.line, pick.side)} partidas seguidas`}><FaFire/><b>{getHitStreak(pick.historyValues, pick.line, pick.side)}x</b></span>}
            <span className="top-prop-hit">{pick.hitRatePct}%<small>HR</small></span>
          </button>;
        })}{topProps.length === 0 && (isLoading ? <PropListSkeleton/> : <p className="news-empty">Nenhuma prop com histórico disponível.</p>)}</div>
      </section>
      {activePick && <PropDetail pick={activePick} onClose={() => setActivePick(null)} />}
      <div className="home-bottom-space" />
    </main>
  );
}

function PropListSkeleton() { return <div className="home-prop-skeletons" aria-label="Carregando props" aria-busy="true">{[0,1,2].map(row => <div className="skeleton-pick" key={row}><span className="skeleton skeleton-avatar"/><span className="skeleton-lines"><i className="skeleton"/><i className="skeleton"/></span><span className="skeleton skeleton-rate"/></div>)}</div>; }
function getHitStreak(values: number[] | undefined, line: number, side: Side) { let streak = 0; for (const value of values ?? []) { if (value === line || (side === Side.OVER ? value < line : value > line)) break; streak++; } return streak; }
